import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const GOOGLE_CLIENT_ID = '141913349498-iaq4ler8fg86i1j2r1eig2ojqhao6bun.apps.googleusercontent.com';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const googleBtnRef = useRef(null);

  const handleGoogleResponse = useCallback(async (response) => {
    setError('');
    setLoading(true);
    const result = await loginWithGoogle(response.credential);
    if (result.success) navigate('/');
    else setError(result.error);
    setLoading(false);
  }, [loginWithGoogle, navigate]);

  useEffect(() => {
    const initGoogle = () => {
      if (window.google && googleBtnRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });
        window.google.accounts.id.renderButton(
          googleBtnRef.current,
          {
            theme: 'outline',
            size: 'large',
            width: googleBtnRef.current.offsetWidth || 360,
            text: 'signin_with',
            locale: 'pt-BR',
          }
        );
      }
    };

    if (window.google) {
      initGoogle();
    } else {
      const interval = setInterval(() => {
        if (window.google) {
          clearInterval(interval);
          initGoogle();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [handleGoogleResponse]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(username, password);
    if (result.success) navigate('/');
    else setError(result.error);
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        {/* Painel lateral institucional */}
        <aside className="login-brand">
          <div className="brand-top">
            <div className="brand-mark">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 2v6l-4 8a4 4 0 0 0 4 6h6a4 4 0 0 0 4-6l-4-8V2"/>
                <path d="M9 2h6"/>
                <path d="M7 16h10"/>
              </svg>
            </div>
            <span className="brand-name">Santa Rita</span>
          </div>

          <div className="brand-body">
            <h2>Sistema de Gestão Laboratorial</h2>
            <p>Plataforma integrada para atendimento, exames, resultados e gestão financeira do laboratório.</p>

            <ul className="brand-features">
              <li>
                <span className="feat-dot" />
                Pacientes, atendimentos e coletas em um só lugar
              </li>
              <li>
                <span className="feat-dot" />
                Resultados, laudos e integração WMI / LifeSys
              </li>
              <li>
                <span className="feat-dot" />
                WhatsApp com agente de IA e envio automático
              </li>
              <li>
                <span className="feat-dot" />
                TISS UNIMED, faturamento e conferência de convênios
              </li>
            </ul>
          </div>

          <div className="brand-foot">
            © {new Date().getFullYear()} Santa Rita · Vip Tecnologia
          </div>
        </aside>

        {/* Formulário */}
        <main className="login-main">
          <div className="login-card">
            <header className="login-card-header">
              <h1>Acesse sua conta</h1>
              <p>Informe suas credenciais para continuar.</p>
            </header>

            <form onSubmit={handleSubmit} className="login-form" noValidate>
              {error && (
                <div className="login-error" role="alert">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="username">Usuário</label>
                <div className="input-wrap">
                  <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="seu.usuario"
                    required
                    autoFocus
                    autoComplete="username"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Senha</label>
                <div className="input-wrap">
                  <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="toggle-pass"
                    onClick={() => setShowPassword(s => !s)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner" />
                    Entrando...
                  </>
                ) : 'Entrar'}
              </button>

              <div className="login-divider"><span>ou continue com</span></div>

              <div ref={googleBtnRef} className="google-btn-wrapper" />
            </form>

            <footer className="login-card-foot">
              Problemas para acessar? Contate o administrador do sistema.
            </footer>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Login;
